import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { FDataGrid } from '../grid';
import {
  BieHistoricalWorkbenchModel,
  HistoricalGapRow,
  HistoricalGapSummary,
  HistoricalIntegrityIssue,
  HistoricalIssueSeverity,
} from './bieHistorical.models';

type Props = {
  workbench: BieHistoricalWorkbenchModel;

  gaps: HistoricalGapRow[];
  gapSummary: HistoricalGapSummary;
  gapRowCount: number;
  gapPaginationModel: GridPaginationModel;
  selectedGapKeys: string[];
  loadingGaps?: boolean;
  downloadingGaps?: boolean;
  onGapPaginationModelChange: (model: GridPaginationModel) => void;
  onSelectedGapKeysChange: (gapkeys: string[]) => void;
  onDownloadAllGaps: () => void;
  onDownloadSelectedGaps: () => void;

  issues: HistoricalIntegrityIssue[];
  issueRowCount: number;
  issuePaginationModel: GridPaginationModel;
  issueSeverity: HistoricalIssueSeverity | '';
  loading?: boolean;
  onIssuePaginationModelChange: (model: GridPaginationModel) => void;
  onSeverityChange: (severity: HistoricalIssueSeverity | '') => void;
  onValidate: () => void;
};

const severityColor = (severity: HistoricalIssueSeverity) => {
  if (severity === 'BLOCKING') return 'error';
  if (severity === 'WARNING') return 'warning';
  return 'info';
};

const formatDateTime = (value: unknown): string => {
  if (!value) return '—';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

class HistoricalIntegrityPanel extends React.PureComponent<Props> {
  private isGapSelected = (gapkey: string): boolean =>
    this.props.selectedGapKeys.includes(gapkey);

  private setGapSelected = (gapkey: string, selected: boolean): void => {
    const keys = new Set(this.props.selectedGapKeys);
    if (selected) keys.add(gapkey);
    else keys.delete(gapkey);
    this.props.onSelectedGapKeysChange(Array.from(keys));
  };

  private currentPageAllSelected = (): boolean =>
    this.props.gaps.length > 0 &&
    this.props.gaps.every((row) => this.isGapSelected(row.gapkey));

  private currentPageSomeSelected = (): boolean => {
    const count = this.props.gaps.filter((row) =>
      this.isGapSelected(row.gapkey),
    ).length;
    return count > 0 && count < this.props.gaps.length;
  };

  private setCurrentPageSelected = (selected: boolean): void => {
    const keys = new Set(this.props.selectedGapKeys);
    this.props.gaps.forEach((row) => {
      if (selected) keys.add(row.gapkey);
      else keys.delete(row.gapkey);
    });
    this.props.onSelectedGapKeysChange(Array.from(keys));
  };

  private gapColumns: GridColDef[] = [
    {
      field: '__selected',
      headerName: '',
      width: 58,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          size="small"
          checked={this.currentPageAllSelected()}
          indeterminate={this.currentPageSomeSelected()}
          onChange={(event) =>
            this.setCurrentPageSelected(event.target.checked)
          }
          inputProps={{ 'aria-label': 'Select current page gaps' }}
        />
      ),
      renderCell: (params: GridRenderCellParams<HistoricalGapRow>) => (
        <Checkbox
          size="small"
          checked={this.isGapSelected(params.row.gapkey)}
          onChange={(event) =>
            this.setGapSelected(params.row.gapkey, event.target.checked)
          }
          inputProps={{ 'aria-label': `Select ${params.row.gapkey}` }}
        />
      ),
    },
    { field: 'fiscalyear', headerName: 'Year', width: 85 },
    { field: 'periodcode', headerName: 'Period', width: 105 },
    { field: 'metriccode', headerName: 'Metric', width: 135 },
    { field: 'metricname', headerName: 'Metric Name', minWidth: 210, flex: 1 },
    { field: 'productcode', headerName: 'Product', width: 120 },
    { field: 'locationcode', headerName: 'Location', width: 120 },
    { field: 'entitycode', headerName: 'Entity', width: 120 },
    { field: 'accountcode', headerName: 'Account', width: 120 },
    { field: 'unitcode', headerName: 'Unit', width: 100 },
    {
      field: 'gapclassname',
      headerName: 'Gap',
      width: 165,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          size="small"
          variant="outlined"
          color={
            String(params.row?.resolutioncode || '').toUpperCase() === 'ESTIMATE'
              ? 'warning'
              : 'error'
          }
          label={params.value || 'Needs Resolution'}
        />
      ),
    },
    {
      field: 'resolutionname',
      headerName: 'Recommended Resolution',
      minWidth: 250,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ whiteSpace: 'normal', lineHeight: 1.35, py: 1 }}>
          {params.value || 'Import actual or enter manually'}
        </Box>
      ),
    },
  ];

  private issueColumns: GridColDef[] = [
    {
      field: 'severitycode',
      headerName: 'Severity',
      width: 115,
      renderCell: (params: GridRenderCellParams) => {
        const value = String(params.value || 'INFO') as HistoricalIssueSeverity;
        return <Chip size="small" color={severityColor(value)} label={value} />;
      },
    },
    { field: 'categoryname', headerName: 'Category', width: 165 },
    { field: 'issuecode', headerName: 'Issue', width: 190 },
    { field: 'message', headerName: 'Description', flex: 1, minWidth: 320 },
    { field: 'fiscalyear', headerName: 'Year', width: 90 },
    { field: 'periodcode', headerName: 'Period', width: 110 },
    { field: 'metriccode', headerName: 'Metric', width: 135 },
    { field: 'productcode', headerName: 'Product', width: 125 },
    { field: 'locationcode', headerName: 'Location', width: 125 },
    { field: 'sourcecode', headerName: 'Source', width: 115 },
    {
      field: 'resolutionhint',
      headerName: 'Resolution',
      flex: 1,
      minWidth: 260,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ whiteSpace: 'normal', lineHeight: 1.35, py: 1 }}>
          {params.value || '—'}
        </Box>
      ),
    },
    {
      field: 'created',
      headerName: 'Detected',
      width: 175,
      valueFormatter: (value: any) => formatDateTime(value),
    },
  ];

  private renderCoverageCard = (
    label: string,
    covered: number,
    expected: number,
    missing: number,
  ) => {
    const complete = expected > 0 && covered >= expected && missing === 0;
    return (
      <Grid size={{ xs: 6, md: 3 }} key={label}>
        <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {covered}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              of {expected}
            </Typography>
          </Stack>
          <Chip
            size="small"
            color={complete ? 'success' : missing > 0 ? 'error' : 'default'}
            label={
              missing > 0
                ? `${missing} missing`
                : complete
                  ? 'Complete'
                  : 'Pending'
            }
            sx={{ mt: 0.75 }}
          />
        </Paper>
      </Grid>
    );
  };

  render() {
    const { workbench, gapSummary } = this.props;
    const { coverage, readiness, validation } = workbench;
    const selectedCount = this.props.selectedGapKeys.length;

    return (
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
          >
            <div>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Coverage & Integrity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Validation is authoritative and must be rerun after every import or manual adjustment.
              </Typography>
            </div>
            <Button
              variant="contained"
              startIcon={<FactCheckIcon />}
              onClick={this.props.onValidate}
              disabled={!workbench.actions.canvalidate || this.props.loading}
            >
              Validate Historical Baseline
            </Button>
          </Stack>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {this.renderCoverageCard('Years', coverage.coveredyears, coverage.expectedyears, coverage.missingyearcount)}
            {this.renderCoverageCard('Periods', coverage.coveredperiods, coverage.expectedperiods, coverage.missingperiodcount)}
            {this.renderCoverageCard('Metrics', coverage.coveredmetrics, coverage.expectedmetrics, coverage.missingmetriccount)}
            {this.renderCoverageCard('Products', coverage.coveredproducts, coverage.expectedproducts, coverage.missingproductcount)}
          </Grid>
        </Paper>

        <Grid container spacing={1.5}>
          {[
            ['Blocking', readiness.blockingcount, 'error'],
            ['Warnings', readiness.warningcount, 'warning'],
            ['Duplicates', coverage.duplicatecount, 'error'],
            ['Unresolved References', coverage.unresolvedcount, 'error'],
            ['Invalid Values', coverage.invalidvaluecount, 'error'],
            ['Missing Units', coverage.missingunitcount, 'warning'],
            ['Orphan Facts', coverage.orphanfactcount, 'error'],
          ].map(([label, value, color]) => (
            <Grid size={{ xs: 6, sm: 4, lg: 2 }} key={String(label)}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {Number(value)}
                </Typography>
                <Chip
                  size="small"
                  color={Number(value) > 0 ? color as any : 'success'}
                  label={Number(value) > 0 ? 'Review' : 'Clear'}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>

        {validation.validatedat ? (
          <Alert severity={readiness.blockingcount > 0 ? 'error' : readiness.warningcount > 0 ? 'warning' : 'success'}>
            Last validation: {formatDateTime(validation.validatedat)}
            {validation.validatedby ? ` by ${validation.validatedby}` : ''}.{' '}
            {validation.blockingcount} blocking, {validation.warningcount} warning and {validation.infocount} information issue(s).
          </Alert>
        ) : (
          <Alert severity="info">This Planning Cycle has not yet been validated.</Alert>
        )}

        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Historical Gaps
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Matrix-derived historical observations requiring actual import, manual entry or controlled estimation.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip size="small" label={`${gapSummary.gapcount} gap(s)`} />
                <Chip size="small" variant="outlined" label={`${gapSummary.yearcount} year(s)`} />
                <Chip size="small" variant="outlined" label={`${gapSummary.metriccount} metric(s)`} />
                {gapSummary.estimatecount > 0 && (
                  <Chip size="small" color="warning" variant="outlined" label={`${gapSummary.estimatecount} estimate eligible`} />
                )}
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                disabled={
                  this.props.downloadingGaps ||
                  this.props.loadingGaps ||
                  gapSummary.gapcount === 0
                }
                onClick={this.props.onDownloadAllGaps}
              >
                Download All Gaps
              </Button>

              <Button
                variant="contained"
                startIcon={<DownloadOutlinedIcon />}
                disabled={
                  this.props.downloadingGaps ||
                  this.props.loadingGaps ||
                  selectedCount === 0
                }
                onClick={this.props.onDownloadSelectedGaps}
              >
                Download Selected Gaps{selectedCount > 0 ? ` (${selectedCount})` : ''}
              </Button>
            </Stack>
          </Stack>

          <FDataGrid
            title=""
            rows={this.props.gaps}
            columns={this.gapColumns}
            loading={this.props.loadingGaps}
            height={480}
            getRowId={(row: HistoricalGapRow) => row.gapkey}
            paginationMode="server"
            rowCount={this.props.gapRowCount}
            paginationModel={this.props.gapPaginationModel}
            onPaginationModelChange={this.props.onGapPaginationModelChange}
            pageSizeOptions={[25, 50, 100, 250]}
            showQuickFilter={false}
            emptyState={{
              title: 'No Historical Gaps',
              message: 'The authoritative Historical Matrix found no workbook-remediable gaps in the current historical analysis window.',
            }}
          />
        </Paper>

        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <div>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Validation Issues
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drill down from coverage totals to the exact metric, product, location and period requiring correction.
              </Typography>
            </div>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="historical-severity-label">Severity</InputLabel>
              <Select
                labelId="historical-severity-label"
                label="Severity"
                value={this.props.issueSeverity}
                onChange={(event) =>
                  this.props.onSeverityChange(
                    event.target.value as HistoricalIssueSeverity | '',
                  )
                }
              >
                <MenuItem value="">All Severities</MenuItem>
                <MenuItem value="BLOCKING">Blocking</MenuItem>
                <MenuItem value="WARNING">Warning</MenuItem>
                <MenuItem value="INFO">Information</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <FDataGrid
            title=""
            rows={this.props.issues}
            columns={this.issueColumns}
            loading={this.props.loading}
            height={500}
            getRowId={(row: HistoricalIntegrityIssue) => row.rid}
            paginationMode="server"
            rowCount={this.props.issueRowCount}
            paginationModel={this.props.issuePaginationModel}
            onPaginationModelChange={this.props.onIssuePaginationModelChange}
            pageSizeOptions={[10, 25, 50, 100]}
            showQuickFilter={false}
            emptyState={{
              title: validation.validatedat ? 'No Validation Issues' : 'Validation Not Run',
              message: validation.validatedat
                ? 'No issues match the selected filter.'
                : 'Run validation to create the authoritative issue register.',
            }}
          />
        </Paper>
      </Stack>
    );
  }
}

export default HistoricalIntegrityPanel;
